import { Trans } from "@lingui/macro";
import { NumberField } from "components/fields/NumberField";
import { FormGroup } from "components/FormGroup";
import { useField, yupToFormErrors } from "formik";
import React, { FunctionComponent } from "react";
import * as Yup from "yup";

type FieldType =
  | "kapitaalaflossing"
  | "bedragLening"
  | "jaarLening"
  | "KTLJaarNaLening"
  | "premie"
  | "verzekerdBedrag"
  | "verzekerdBedragAangever"
  | "verzekerdBedragPartner";

export interface Props {
  code: string;
  fields: FieldType[];
  onSubmit: (values: { [key in FieldType]: number }) => void;
}

const jaarLeningErrorMessage: any = (
  <Trans>Het jaar van lening moet zich bevinden tussen 1989 en {new Date().getFullYear() - 1}.</Trans>
);

const schema = Yup.object({
  jaarLening: Yup.number()
    .typeError(jaarLeningErrorMessage)
    .min(1989, jaarLeningErrorMessage)
    .max(new Date().getFullYear() - 1, jaarLeningErrorMessage),
});

const validate = (values: any): any => {
  try {
    schema.validateSync(values);
    return;
  } catch (e) {
    return yupToFormErrors(e);
  }
};

export const HelpForm: FunctionComponent<Props> = ({ code, onSubmit, fields }) => {
  const prefix = `help.c${code}`;
  const [{ value = {} }, , { setError }] = useField({ name: prefix, validate });
  const inputRef = React.useRef<HTMLInputElement>();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const errors = validate(value);
    if (errors) {
      return setError(errors);
    }
    onSubmit(value);
  };

  React.useEffect(() => {
    if (inputRef.current) {
      const ref = inputRef.current;
      setTimeout(() => {
        ref.focus();
      }, 0);
    }
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      {fields.map((field, index) => {
        const ref = index === 0 ? inputRef : undefined;
        switch (field) {
          case "kapitaalaflossing":
            return (
              <FormGroup name={`${prefix}.kapitaalaflossing`} key={field}>
                <label htmlFor={`${prefix}.kapitaalaflossing`}>
                  <Trans>Kapitaalaflossing</Trans>
                </label>
                <NumberField name={`${prefix}.kapitaalaflossing`} getInputRef={ref} />
              </FormGroup>
            );
          case "bedragLening":
            return (
              <FormGroup name={`${prefix}.bedragLening`} key={field}>
                <label htmlFor={`${prefix}.bedragLening`}>
                  <Trans>Hypothecair ontleend bedrag</Trans>
                </label>
                <NumberField name={`${prefix}.bedragLening`} getInputRef={ref} />
              </FormGroup>
            );
          case "jaarLening":
            return (
              <FormGroup name={`${prefix}.jaarLening`} key={field}>
                <label htmlFor={`${prefix}.jaarLening`}>
                  <Trans>Jaar van lening</Trans>
                </label>
                <NumberField name={`${prefix}.jaarLening`} max={4} decimalScale={0} getInputRef={ref} />
              </FormGroup>
            );
          case "KTLJaarNaLening":
            return (
              <FormGroup name={`${prefix}.KTLJaarNaLening`} key={field}>
                <label htmlFor={`${prefix}.KTLJaarNaLening`}>
                  <Trans>
                    Kinderen ten laste op 01/01/
                    {!!value.jaarLening ? Number.parseInt(value.jaarLening) + 1 : "xxxx"}
                  </Trans>
                </label>
                <NumberField name={`${prefix}.KTLJaarNaLening`} min={0} decimalScale={0} getInputRef={ref} />
              </FormGroup>
            );
          case "premie":
            return (
              <FormGroup name={`${prefix}.premie`} key={field}>
                <label htmlFor={`${prefix}.premie`}>
                  <Trans>Premie levensverzekering</Trans>
                </label>
                <NumberField name={`${prefix}.premie`} getInputRef={ref} />
              </FormGroup>
            );
          case "verzekerdBedragAangever":
            return (
              <FormGroup name={`${prefix}.verzekerdBedragAangever`} key={field}>
                <label htmlFor={`${prefix}.verzekerdBedragAangever`}>
                  <Trans>Verzekerd bedrag aangever</Trans>
                </label>
                <NumberField name={`${prefix}.verzekerdBedragAangever`} getInputRef={ref} />
              </FormGroup>
            );
          case "verzekerdBedragPartner":
            return (
              <FormGroup name={`${prefix}.verzekerdBedragPartner`} key={field}>
                <label htmlFor={`${prefix}.verzekerdBedragPartner`}>
                  <Trans>Verzekerd bedrag partner</Trans>
                </label>
                <NumberField name={`${prefix}.verzekerdBedragPartner`} getInputRef={ref} />
              </FormGroup>
            );
          case "verzekerdBedrag":
            return (
              <FormGroup name={`${prefix}.verzekerdBedrag`} key={field}>
                <label htmlFor={`${prefix}.verzekerdBedrag`}>
                  <Trans>Verzekerd bedrag</Trans>
                </label>
                <NumberField name={`${prefix}.verzekerdBedrag`} getInputRef={ref} />
              </FormGroup>
            );
          default:
            return null;
        }
      })}

      <button type="submit">
        <Trans>Voeg toe</Trans>
      </button>
    </form>
  );
};

HelpForm.displayName = "HelpForm";
